import type { Numeric } from 'decimal.js-light';
import Decimal from 'decimal.js-light';

import { cleanRawDecimalValue } from './clean-raw-decimal-value';
import { formatFixed } from './format-fixed';

/**
 * Compute a percent change from one value to another value.
 */
export function computePercentChange(from: Numeric, to: Numeric): number {
	from = new Decimal(cleanRawDecimalValue(from));
	to = new Decimal(cleanRawDecimalValue(to));

	try {
		return to
			.minus(from)
			.dividedBy(from)
			.times(100)
			.toDecimalPlaces(0)
			.toNumber();
	} catch {
		const cmp = to.comparedTo(0);
		if (cmp === -1 /* to < 0 */) return Number.NEGATIVE_INFINITY;
		if (cmp === 1 /* to > 0 */) return Number.POSITIVE_INFINITY;
		return 0;
	}
}

const compactFormatter = Intl.NumberFormat('en-US', {
	notation: 'compact',
	maximumFractionDigits: 1,
	maximumSignificantDigits: 3,
});

/**
 * Format a percent change from one value to another value.
 *
 * Note: This returns the sign and magnitude separately because some
 *       UIs display increases and decreases in special ways.
 */
export function formatPercentChange(
	change: number | 'Infinity' | '-Infinity'
): [1 | 0 | -1, string | null] {
	if (change === Number.POSITIVE_INFINITY || change === 'Infinity') {
		return [1, null];
	}
	if (change === Number.NEGATIVE_INFINITY || change === '-Infinity') {
		return [-1, null];
	}

	const decimalChange = new Decimal(change);

	const sign = decimalChange.comparedTo(0);
	const magnitude = decimalChange.absoluteValue();

	const shouldAbbreviate = magnitude.greaterThanOrEqualTo(10_000);
	const formattedMagnitude = shouldAbbreviate
		? compactFormatter.format(magnitude.toNumber())
		: formatFixed(magnitude);

	return [sign, `${formattedMagnitude}%`];
}
